import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Send, Undo2, Printer, Usb, RotateCcw, AlertTriangle, Package } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import apiClient from '../../../services/apiClient';
import * as printerApi from '../../../services/printerApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '../../../utils/tsplGenerator';
import { useUSNReplacement } from '../../../hooks/useUSNReplacement';
import { fetchComboLabelData } from '../../../utils/comboLabelData';

const API = process.env.REACT_APP_BACKEND_URL;
const PRINTER_MODEL = 'TSC_TE210';
const COPIES_PER_LABEL = 1;
// Match V1 ComboAssemblyStation defaults so combo labels render identically.
const ARIAL_LINE_SPACING_MULTIPLIER = 2.5;

export default function ComboV2DistributorStation() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { openReplaceDialog, replaceBatch } = useUSNReplacement();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qtyByStaff, setQtyByStaff] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [me, setMe] = useState(null);  // current user (for admin gating)

  // Sessions in this plan + USB reprint state
  const [sessions, setSessions] = useState([]);
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [reprinting, setReprinting] = useState(null);  // session_id when in flight
  // Box-label specific state (Step 2 of the print flow) — parity with V1 + V2 PP Station
  const [boxPrinterDevice, setBoxPrinterDevice] = useState(null);
  const [pendingBoxData, setPendingBoxData] = useState(null);
  const [showBoxAlert, setShowBoxAlert] = useState(false);

  // Released-handovers admin tool state
  const [releasedRows, setReleasedRows] = useState([]);
  const [showReleased, setShowReleased] = useState(false);
  const [releasedLoading, setReleasedLoading] = useState(false);

  // "Picked-but-not-handed-over" — bundles the picker scanned that never
  // made it onto the distributor floor. Operator can tick + Confirm to
  // flip them onto the floor (status → PICKED_FOR_COMBO_V2 + plan tag).
  const [pnhRows, setPnhRows] = useState([]);
  const [pnhLoading, setPnhLoading] = useState(false);
  const [pnhSelected, setPnhSelected] = useState(new Set());
  const [pnhConfirming, setPnhConfirming] = useState(false);

  // Reprint Modal state — drill into a session's handovers and pick the
  // exact USN(s) to reprint instead of reprinting the entire 148-pcs
  // session and reissuing QR codes on the 130+ stickers that are already
  // good and stuck on physical pieces.
  const [reprintModal, setReprintModal] = useState(null);  // { sessionId, comboBarcode, handovers, loading }
  const [reprintSelected, setReprintSelected] = useState(new Set());

  const openReprintModal = async (sessionId, comboBarcode) => {
    setReprintModal({ sessionId, comboBarcode, handovers: [], loading: true });
    setReprintSelected(new Set());
    try {
      const r = await apiClient.get(`/combo/assembly/sessions/${sessionId}/handovers-with-items`);
      setReprintModal({
        sessionId,
        comboBarcode,
        sessionInfo: r.data,
        handovers: r.data.handovers || [],
        loading: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load handovers');
      setReprintModal(null);
    }
  };

  const toggleReprintUsn = (code) => {
    setReprintSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const toggleReprintHandover = (handover) => {
    setReprintSelected((prev) => {
      const next = new Set(prev);
      const handoverCodes = (handover.items || [])
        .map((it) => it.real_usn_code || it.combo_usn)
        .filter(Boolean);
      const allSelected = handoverCodes.every((c) => next.has(c));
      if (allSelected) {
        handoverCodes.forEach((c) => next.delete(c));
      } else {
        handoverCodes.forEach((c) => next.add(c));
      }
      return next;
    });
  };

  const reprintSelectedUsns = async () => {
    if (!reprintModal) return;
    if (!reprintSelected.size) {
      toast.warning('Tick the USNs you want to reprint first');
      return;
    }
    const codes = Array.from(reprintSelected);
    await reprintSession(reprintModal.sessionId, reprintModal.comboBarcode, codes);
    setReprintModal(null);
    setReprintSelected(new Set());
  };

  const unRelease = async (h) => {
    const confirmMsg = h.un_release_safe
      ? `Revert handover ${h.handover_id} to ACTIVE?\n\nStaff: ${h.pp_staff_name || h.pp_staff_id}\nQty assigned: ${h.qty_assigned}\nConsumed USNs so far: ${h.consumed_usns_count}`
      : `⚠ Staff ${h.pp_staff_name || h.pp_staff_id} already has ${h.peer_active_handovers_for_staff} ACTIVE handover(s) on this plan. Un-releasing this older one will leave it hidden by the PP-station UI.\n\nProceed anyway?`;
    if (!window.confirm(confirmMsg)) return;
    const reason = window.prompt('Reason (audit) — required:', '');
    if (!reason || !reason.trim()) { toast.error('Reason is required'); return; }
    try {
      const force = !h.un_release_safe;
      await apiClient.post(
        `/combo-v2/distributor/handover/${h.handover_id}/un-release?force=${force}&reason=${encodeURIComponent(reason)}`
      );
      toast.success(`Reverted ${h.handover_id} → ACTIVE`);
      loadReleased();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Un-release failed');
    }
  };

  const bulkUnRelease = async () => {
    const safe = releasedRows.filter(r => r.un_release_safe);
    const unsafe = releasedRows.filter(r => !r.un_release_safe);
    if (safe.length === 0) {
      toast.error('No safely-revertible handovers (every released handover\'s staff has another active one on this plan).');
      return;
    }
    const msg = `Revert ${safe.length} RELEASED handover(s) → ACTIVE on this plan?\n\nWill be SKIPPED (staff has another active): ${unsafe.length}\n\nThis is reversible — each handover keeps its release/un-release history.`;
    if (!window.confirm(msg)) return;
    const reason = window.prompt('Reason (audit) — required:', 'Bulk revert: released by mistake');
    if (!reason || !reason.trim()) { toast.error('Reason is required'); return; }
    try {
      const r = await apiClient.post(
        `/combo-v2/distributor/plans/${planId}/un-release-all?force=false&reason=${encodeURIComponent(reason)}`
      );
      toast.success(`Reverted ${r.data.reverted_count} handover(s); skipped ${r.data.skipped_count}`);
      loadReleased();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk un-release failed');
    }
  };

  const loadSessions = async () => {
    try {
      const r = await apiClient.get(`/combo-v2/pp-station/plans/${planId}/sessions`);
      setSessions(r.data.sessions || []);
    } catch (e) {
      console.error('Failed to load sessions for reprint', e);
    }
  };

  useEffect(() => {
    setUsbSupported(printerApi.isWebUSBSupported());
    (async () => {
      try {
        const r = await apiClient.get('/main-labels/templates');
        setTemplates(r.data.templates || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const findTemplateByName = (templateName) => {
    if (!templateName || !templates.length) return null;
    const normalize = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').replace(/\s*\(\s*/g, '(').replace(/\s*\)\s*/g, ')').trim();
    const target = normalize(templateName);
    let match = templates.find(t => normalize(t.template_name) === target);
    if (match) return match;
    match = templates.find(t => target.includes(normalize(t.template_name)) || normalize(t.template_name).includes(target));
    return match || null;
  };

  const connectUSBPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected: ${device.productName || 'USB Printer'}`);
      return device;
    } catch (e) {
      toast.error('Failed to connect USB printer');
      return null;
    }
  };

  const connectBoxPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setBoxPrinterDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'} (Box Labels)`);
      return device;
    } catch (e) {
      toast.error('Failed to connect box label printer');
      return null;
    }
  };

  // Build TSPL commands for either product or box labels and stream to a USB printer.
  // Mirrors V1 ComboAssemblyStation.executeUsbPrint (and V2 PP Station post-fix).
  const executeUsbPrint = async (labelDataMap, defaultTemplate, usns, device, printType, comboBarcode) => {
    const printerConfig = PRINTER_CONFIGS[PRINTER_MODEL];
    let allCommands = '';
    let totalLabels = 0;

    for (const usn of usns) {
      try {
        const cachedData = labelDataMap[usn.barcode || comboBarcode];
        if (!cachedData) continue;

        const labelData = cachedData.label_data || {};
        const mrpFormatted = cachedData.mrp_formatted || `Rs.${labelData.mrp || 0}`;

        if (printType === 'product') {
          const templateName = usn.print_template || 'Standard';
          const template = findTemplateByName(templateName) || defaultTemplate;
          for (let copy = 0; copy < COPIES_PER_LABEL; copy++) {
            allCommands += generateTsplFromLabelData(
              labelData, template, usn.usn_code, mrpFormatted,
              usn.usn_code, printerConfig, template?.template_type,
              usn.pair_number, ARIAL_LINE_SPACING_MULTIPLIER
            );
            totalLabels++;
          }
        } else if (printType === 'box' && usn.box_template) {
          const boxTemplate = findTemplateByName(usn.box_template);
          const tpl = boxTemplate || defaultTemplate;
          if (tpl) {
            for (let copy = 0; copy < COPIES_PER_LABEL; copy++) {
              allCommands += generateTsplFromLabelData(
                labelData, tpl, usn.usn_code, mrpFormatted,
                usn.usn_code, printerConfig, boxTemplate?.template_type || 'box',
                usn.pair_number, ARIAL_LINE_SPACING_MULTIPLIER
              );
              totalLabels++;
            }
          }
        }
      } catch (usnError) {
        console.error(`Error generating label for ${usn.usn_code}:`, usnError);
      }
    }

    if (!allCommands) {
      if (printType === 'box') {
        toast.warning('No box labels to print');
        return 0;
      }
      throw new Error('Failed to generate label commands');
    }

    if (device) {
      await printerApi.sendToUSBPrinter(device, allCommands);
    }
    return totalLabels;
  };

  // Handle box sticker confirmation
  const handleBoxPrintConfirm = async () => {
    setShowBoxAlert(false);
    if (!pendingBoxData) return;
    let printerToUse = boxPrinterDevice;
    if (!printerToUse) {
      toast.info('Please select a printer for box labels');
      printerToUse = await connectBoxPrinter();
      if (!printerToUse) {
        setShowBoxAlert(true);
        return;
      }
    }
    try {
      const { labelInfo, labelDataMap, defaultTemplate } = pendingBoxData;
      const boxCount = await executeUsbPrint(
        labelDataMap, defaultTemplate, labelInfo.usns, printerToUse, 'box', labelInfo.combo_barcode
      );
      if (boxCount > 0) toast.success(`🖨️ Printed ${boxCount} box label(s)`);
    } catch (e) {
      toast.error(e.message || 'Box label print failed');
    } finally {
      setPendingBoxData(null);
    }
  };

  const handleBoxPrintCancel = () => {
    setShowBoxAlert(false);
    setPendingBoxData(null);
  };

  const reprintSession = async (sessionId, comboBarcode, usnCodes = null) => {
    let device = usbDevice;
    if (!device) device = await connectUSBPrinter();
    if (!device) return;
    setReprinting(sessionId);
    try {
      // Force reprint=true so already-printed combos are returned with their
      // existing real_usn_code + pair_number from `combo_assembled_items`.
      // When usnCodes is provided, scope the reprint to ONLY those stickers
      // (per-handover / per-USN reprint UX).
      const qs = new URLSearchParams({ reprint: 'true' });
      if (usnCodes && usnCodes.length) {
        qs.set('usn_codes', usnCodes.join(','));
      }
      const labelResp = await apiClient.get(
        `/combo/assembly/sessions/${sessionId}/label-data?${qs.toString()}`
      );
      const labelInfo = labelResp.data;
      if (!labelInfo.usn_count) {
        toast.info(usnCodes
          ? 'None of the selected USNs match printed items in this session'
          : 'No printed combo USNs found for this session');
        setReprinting(null);
        return;
      }
      // Price check in the plan's channel BEFORE replacing USNs — a missing rate stops here (no blank-MRP stickers).
      const { labelDataMap, defaultTemplate } = await fetchComboLabelData(labelInfo, templates, findTemplateByName);
      // REPLACE: deactivate old combo USNs and claim fresh ones.
      const oldCodes = labelInfo.usns.map(u => u.usn_code).filter(Boolean);
      const dialog = await openReplaceDialog({
        count: oldCodes.length,
        sourceFlow: 'combo_v2_distributor_reprint',
        subjectLabel: `Combo session ${sessionId} — ${oldCodes.length} USN(s)`,
      });
      if (!dialog) { setReprinting(null); return; }
      const batch = await replaceBatch(oldCodes, {
        reason: dialog.reason,
        sourceFlow: 'combo_v2_distributor_reprint',
        linkedDocuments: { session_id: sessionId, plan_id: planId, source_page: 'ComboV2:Distributor' },
      });
      if (!batch) { setReprinting(null); return; }
      if (batch.failed_count) {
        const firstDetail = batch.failures?.[0]?.detail || 'unknown reason';
        toast.warning(
          batch.failed_count === 1
            ? `Replace failed: ${firstDetail}`
            : `${batch.failed_count} replace failures — first: ${firstDetail}`,
          { duration: 10000 }
        );
        console.warn('USN replace failures:', batch.failures);
      }
      const oldToNew = Object.fromEntries(batch.replacements.map(r => [r.old_usn, r.new_usn]));
      labelInfo.usns = labelInfo.usns.map(u => ({
        ...u,
        usn_code: oldToNew[u.usn_code] || u.usn_code,
      }));

      // Step 1: product labels
      const productCount = await executeUsbPrint(
        labelDataMap, defaultTemplate, labelInfo.usns, device, 'product', labelInfo.combo_barcode
      );
      toast.success(`🖨️ Reprinted ${productCount} product label(s)`);

      // 2026-05-20 (rev) — Reprint must also re-issue the BOX sticker just
      // like the original PP-Station print flow. Yesterday's change tried
      // to skip the "combo USN" sticker on reprint but accidentally
      // suppressed the box-sticker dialog entirely. The operator confirmed
      // the box sticker IS required on reprint, so the box-print dialog
      // is queued here whenever the SKU has a box template configured.
      if (labelInfo.has_box_template) {
        setPendingBoxData({ labelInfo, labelDataMap, defaultTemplate });
        setShowBoxAlert(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Reprint failed');
    } finally {
      setReprinting(null);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const tk = localStorage.getItem('session_token');
      const r = await axios.get(`${API}/api/combo-v2/distributor/plans/${planId}/floor-state`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setState(r.data);
      // Initialize qty inputs with suggested values
      const init = {};
      for (const s of r.data.staff || []) {
        if (s.is_free) init[s.staff_id] = s.suggested_qty || 0;
      }
      setQtyByStaff(init);
    } catch (e) {
      toast.error('Failed to load floor state');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadSessions(); }, [planId]);
  // Fetch current user once to gate the admin-only Reset button.
  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get('/auth/me');
        setMe(r.data?.user || r.data);
      } catch (e) { /* not logged in or non-fatal */ }
    })();
  }, []);

  const loadPickedNotHandedOver = async () => {
    setPnhLoading(true);
    try {
      const r = await apiClient.get(`/combo-v2/distributor/plans/${planId}/picked-not-handed-over`);
      setPnhRows(r.data?.picked_not_handed_over || []);
      setPnhSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load picked-but-not-handed-over list');
    } finally {
      setPnhLoading(false);
    }
  };

  useEffect(() => { loadPickedNotHandedOver(); }, [planId]);

  const togglePnh = (code) => {
    setPnhSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const confirmHandoverToTable = async () => {
    if (pnhSelected.size === 0) return;
    const codes = Array.from(pnhSelected);
    setPnhConfirming(true);
    try {
      const r = await apiClient.post(
        `/combo-v2/distributor/plans/${planId}/confirm-handover-to-table`,
        { bundle_codes: codes },
      );
      const { confirmed_count, skipped_count, skipped } = r.data || {};
      if (confirmed_count) {
        toast.success(`✅ ${confirmed_count} bundle(s) confirmed onto the table`);
      }
      if (skipped_count) {
        const reasons = (skipped || []).map(s => `${s.bundle_code}: ${s.reason}`).join('\n');
        toast.warning(`${skipped_count} bundle(s) skipped\n${reasons}`, { duration: 12000 });
      }
      await Promise.all([load(), loadPickedNotHandedOver()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirm handover failed');
    } finally {
      setPnhConfirming(false);
    }
  };

  const isAdmin = (me?.role || '').toLowerCase() === 'admin' || (me?.role || '').toLowerCase() === 'super_admin';

  const resetPlan = async () => {
    const stage1 = window.confirm(
      `⚠ ADMIN HARD RESET — Plan ${planId}\n\n`
      + `This will VOID every handover, DELETE every consumed-USN record and `
      + `every assembled-combo record on this plan, and reset each session back `
      + `to assembled_quantity=0 / status=ASSEMBLY_PENDING.\n\n`
      + `Picked bundles stay on the floor (PICKED_FOR_COMBO_V2). The distributor `
      + `can re-assign and PP staff start fresh.\n\n`
      + `Refused if any combo labels were already PRINTED (override available).\n\n`
      + `Proceed?`
    );
    if (!stage1) return;
    const reason = window.prompt('Reason (audit) — required, min 3 chars:', '');
    if (!reason || reason.trim().length < 3) { toast.error('Reason required (≥ 3 chars)'); return; }
    setResetting(true);
    try {
      const r = await apiClient.post(
        `/combo-v2/distributor/plans/${planId}/reset-to-pick-state`,
        { reason: reason.trim(), force_with_printed_labels: false }
      );
      toast.success(
        `Reset complete (${r.data.reset_id}) — `
        + `voided ${r.data.handovers_voided} handover(s), `
        + `deleted ${r.data.consumed_usns_deleted} consumed-USN(s) + `
        + `${r.data.combo_assembled_items_deleted} combo item(s), `
        + `cleared ${r.data.bundles_consumption_cleared} bundle(s).`
      );
      load();
      loadSessions();
    } catch (e) {
      // If 409 (printed labels exist) — re-prompt with force flag.
      if (e.response?.status === 409) {
        const ok2 = window.confirm(
          `${e.response.data.detail}\n\nProceed WITH force_with_printed_labels=true?`
        );
        if (!ok2) { setResetting(false); return; }
        try {
          const r = await apiClient.post(
            `/combo-v2/distributor/plans/${planId}/reset-to-pick-state`,
            { reason: reason.trim() + ' [force-printed]', force_with_printed_labels: true }
          );
          toast.success(`Reset complete (forced) — ${r.data.reset_id}`);
          load();
          loadSessions();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Force reset failed');
        }
      } else {
        toast.error(e.response?.data?.detail || 'Reset failed');
      }
    } finally { setResetting(false); }
  };

  const handover = async (staff) => {
    const qty = parseInt(qtyByStaff[staff.staff_id] || 0, 10);
    if (qty <= 0) { toast.error('Enter a qty > 0'); return; }
    try {
      setSubmitting(staff.staff_id);
      const tk = localStorage.getItem('session_token');
      const r = await axios.post(`${API}/api/combo-v2/distributor/handover`, {
        plan_id: planId,
        pp_staff_id: staff.staff_id,
        pp_staff_name: staff.staff_name,
        qty,
      }, { headers: { Authorization: `Bearer ${tk}` } });
      toast.success(`Handover ${r.data.handover_id} created — ${qty} pcs to ${staff.staff_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Handover failed');
    } finally { setSubmitting(null); }
  };

  const release = async (h) => {
    const remaining = h.qty_assigned - h.qty_scanned;
    const hasScans = (h.qty_scanned || 0) > 0;
    if (!window.confirm(
      hasScans
        ? `⚠ Handover ${h.handover_id} already has ${h.qty_scanned} scanned pcs. Releasing it will hide the work from ${h.pp_staff_name || 'staff'}.\n\nReleasing the handover does NOT undo the scans — they stay consumed.\n\nProceed?`
        : `Release handover ${h.handover_id} (${remaining} pcs unscanned)?`
    )) return;
    try {
      const tk = localStorage.getItem('session_token');
      await axios.post(`${API}/api/combo-v2/distributor/handover/${h.handover_id}/release`, {}, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      toast.success('Released');
      load();
    } catch (e) {
      // 409 = guardrail tripped. Offer admin force-override.
      if (e.response?.status === 409) {
        const reason = window.prompt(
          `${e.response.data.detail}\n\nType a reason to ADMIN-FORCE the release:`,
          ''
        );
        if (!reason || !reason.trim()) { toast.error('Cancelled — reason required for force release'); return; }
        try {
          const tk = localStorage.getItem('session_token');
          await axios.post(
            `${API}/api/combo-v2/distributor/handover/${h.handover_id}/release?force=true&reason=${encodeURIComponent(reason)}`,
            {},
            { headers: { Authorization: `Bearer ${tk}` } },
          );
          toast.success('Released (forced)');
          load();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Force release failed');
        }
      } else {
        toast.error(e.response?.data?.detail || 'Release failed');
      }
    }
  };

  const recomputeScanned = async () => {
    try {
      const r = await apiClient.post(`/combo-v2/distributor/plans/${planId}/recompute-handover-scanned`);
      const fc = r.data?.fixed_count || 0;
      toast.success(fc > 0 ? `Recomputed ${fc} handover(s)` : 'No handovers needed recomputation');
      load();
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    }
  };

  const loadReleased = async () => {
    setReleasedLoading(true);
    try {
      const r = await apiClient.get(`/combo-v2/distributor/plans/${planId}/released-handovers`);
      setReleasedRows(r.data?.released_handovers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load released handovers');
      setReleasedRows([]);
    } finally {
      setReleasedLoading(false);
    }
  };

  if (loading || !state) {
    return <div className="min-h-screen bg-[#FFFBF5] p-12"><Loader2 className="h-6 w-6 animate-spin"/></div>;
  }

  const committed = (state.active_handovers || []).reduce((a, h) => a + Math.max(0, h.qty_assigned - h.qty_scanned), 0);
  const free = Math.max(0, state.floor_total_pcs - committed);
  const needed = state.pcs_needed_for_assembly ?? 0;
  const distributable = state.pcs_distributable ?? 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="distributor-station-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2/distributor')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{state.plan_name}</h1>
            <p className="text-xs text-stone-500 font-mono">{state.plan_id} • {state.plan_status}</p>
          </div>
          <Button className="ml-auto" variant="outline" onClick={recomputeScanned} data-testid="recompute-scanned-btn" title="Backfill qty_scanned for handovers affected by the pre-fix bug (scanned was tracked in combos instead of pieces)">
            Recompute Scanned
          </Button>
          <Button
            variant="outline"
            onClick={() => { const next = !showReleased; setShowReleased(next); if (next) loadReleased(); }}
            data-testid="toggle-released-btn"
            title="View / revert handovers that were released — useful when released by mistake."
          >
            <Undo2 className="h-4 w-4 mr-1" /> {showReleased ? 'Hide Released' : 'Released Handovers'}
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={resetPlan}
              disabled={resetting}
              data-testid="reset-plan-btn"
              title="ADMIN ONLY — Hard-reset the plan back to its post-pick state. Voids handovers, deletes consumed USNs and combo items, resets sessions."
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <RotateCcw className="h-4 w-4 mr-1"/>}
              Reset Plan (Admin)
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/wms/combo-v2/return-excess/${planId}`)} data-testid="return-excess-btn">
            <Undo2 className="h-4 w-4 mr-1" /> Return Leftover
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Card className="p-4">
            <div className="text-xs text-stone-500">Floor Total</div>
            <div className="text-2xl font-bold" data-testid="floor-total">{state.floor_total_pcs}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-stone-500">Committed (Active)</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="committed-total">{committed}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-stone-500">Free to Hand Over</div>
            <div className="text-2xl font-bold text-green-700" data-testid="free-total">{free}</div>
          </Card>
          <Card className="p-4 border-stone-900/20 bg-stone-50">
            <div className="text-xs text-stone-500">Pcs Needed for Assembly</div>
            <div className="text-2xl font-bold text-stone-900" data-testid="needed-total">{needed}</div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              {needed === 0
                ? 'All sessions assembled'
                : `Suggesting ${distributable} pcs across free staff`}
            </div>
          </Card>
        </div>

        {needed > 0 && state.floor_total_pcs > needed && (
          <div className="mb-4 px-4 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="excess-floor-banner">
            Floor has <b>{state.floor_total_pcs - needed} extra pcs</b> beyond this plan's
            assembly demand. Suggestions below cover only the {needed} pcs needed —
            leftover stays on floor for Return-to-SFG or a future plan.
          </div>
        )}

        {/* Picked-but-not-handed-over: bundles the picker scanned which never */}
        {/* made it onto the floor. Tick + Confirm to flip them onto the table. */}
        {pnhRows.length > 0 && (
          <Card className="mb-4 border-amber-300" data-testid="picked-not-handed-over-card">
            <div className="p-4 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
              <div>
                <div className="font-semibold text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Picked but not on the table — {pnhRows.length} bundle(s)
                </div>
                <p className="text-xs text-amber-800 mt-1">
                  These bundles were marked picked on the slip but their inventory
                  status didn't update to <span className="font-mono">PICKED_FOR_COMBO_V2</span>.
                  Tick the ones physically present at the table and click
                  <strong> Confirm Handover to Table</strong> to add them to the floor.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadPickedNotHandedOver}
                disabled={pnhLoading}
                data-testid="pnh-refresh-btn"
              >
                {pnhLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-stone-100 text-stone-600">
                  <tr>
                    <th className="w-8 p-2 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-emerald-600"
                        checked={pnhSelected.size > 0 && pnhSelected.size === pnhRows.filter(r => r.current_status !== 'CONSUMED' && r.current_status !== 'REVERSED' && r.current_status !== 'NOT_FOUND_IN_INVENTORY').length}
                        onChange={() => {
                          const eligible = pnhRows.filter(r =>
                            r.current_status !== 'CONSUMED' &&
                            r.current_status !== 'REVERSED' &&
                            r.current_status !== 'NOT_FOUND_IN_INVENTORY'
                          ).map(r => r.bundle_code);
                          if (pnhSelected.size === eligible.length) setPnhSelected(new Set());
                          else setPnhSelected(new Set(eligible));
                        }}
                        data-testid="pnh-select-all"
                      />
                    </th>
                    <th className="text-left p-2">Bundle Code</th>
                    <th className="text-left p-2">SKU</th>
                    <th className="text-right p-2">Pcs</th>
                    <th className="text-left p-2">Current Status</th>
                    <th className="text-left p-2">Current Bin</th>
                    <th className="text-left p-2">Picked By</th>
                  </tr>
                </thead>
                <tbody>
                  {pnhRows.map((r) => {
                    const code = r.bundle_code;
                    const blocked = r.current_status === 'CONSUMED'
                      || r.current_status === 'REVERSED'
                      || r.current_status === 'NOT_FOUND_IN_INVENTORY'
                      || (r.current_plan_tag && r.current_plan_tag !== planId && r.current_status === 'PICKED_FOR_COMBO_V2');
                    return (
                      <tr key={code} className="border-t border-stone-100" data-testid={`pnh-row-${code}`}>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-emerald-600"
                            checked={pnhSelected.has(code)}
                            onChange={() => togglePnh(code)}
                            disabled={blocked}
                            title={blocked ? `Cannot confirm — ${r.current_status}${r.current_plan_tag && r.current_plan_tag !== planId ? ` (tagged to ${r.current_plan_tag})` : ''}` : ''}
                            data-testid={`pnh-cb-${code}`}
                          />
                        </td>
                        <td className="p-2 font-mono">{code}</td>
                        <td className="p-2 font-mono">{r.sku_code || '—'}</td>
                        <td className="p-2 text-right">{r.available_qty}</td>
                        <td className="p-2">
                          <Badge
                            variant="outline"
                            className={blocked ? 'text-rose-700 border-rose-300' : 'text-amber-700 border-amber-300'}
                          >
                            {r.current_status}
                          </Badge>
                          {r.current_plan_tag && r.current_plan_tag !== planId && (
                            <span className="ml-2 text-[11px] text-rose-600">→ {r.current_plan_tag}</span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-stone-500">{r.current_bin || '—'}</td>
                        <td className="p-2 text-stone-600">{r.picked_by_name || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t bg-stone-50 flex items-center justify-between">
              <div className="text-xs text-stone-600">
                <strong>{pnhSelected.size}</strong> bundle(s) selected
              </div>
              <Button
                onClick={confirmHandoverToTable}
                disabled={pnhSelected.size === 0 || pnhConfirming}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="pnh-confirm-btn"
              >
                {pnhConfirming
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Confirming…</>
                  : <><Send className="h-4 w-4 mr-1" /> Confirm Handover to Table ({pnhSelected.size})</>}
              </Button>
            </div>
          </Card>
        )}

        <Card className="mb-4">
          <div className="p-4 border-b font-semibold">Combo PP Staff (today)</div>
          {!state.staff?.length ? (
            <div className="p-12 text-center text-stone-500">No combo PP staff tagged for today. Check the dispatch supervisor page.</div>
          ) : (
            <table className="w-full text-sm" data-testid="staff-table">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3">Staff</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3" title="Live: sum of qty_assigned across this staff's ACTIVE handovers on this plan. Released and completed handovers are excluded so re-assignments don't accumulate.">Active Assigned</th>
                  <th className="text-right p-3" title="Live: sum of qty_scanned across this staff's ACTIVE handovers on this plan.">Active Scanned</th>
                  <th className="text-right p-3" title="Lifetime audit: sum across ALL handovers (active + released + completed). Click 'Released Handovers' to drill in.">Lifetime A/S</th>
                  <th className="text-right p-3">Suggested</th>
                  <th className="text-center p-3">Hand Over</th>
                </tr>
              </thead>
              <tbody>
                {state.staff.map(s => (
                  <tr key={s.staff_id} className="border-t hover:bg-stone-50" data-testid={`staff-row-${s.staff_id}`}>
                    <td className="p-3">
                      <div className="font-medium">{s.staff_name}</div>
                      <div className="text-[11px] text-stone-400 font-mono">{s.staff_id}</div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={s.is_free ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {s.is_free ? 'Free' : `Busy (${s.active_remaining} pcs)`}
                      </Badge>
                    </td>
                    <td className="p-3 text-right" data-testid={`active-assigned-${s.staff_id}`}>{s.active_assigned ?? 0}</td>
                    <td className="p-3 text-right" data-testid={`active-scanned-${s.staff_id}`}>{s.active_scanned ?? 0}</td>
                    <td className="p-3 text-right text-xs text-stone-500">
                      <span title={`Lifetime assigned/scanned across active + released + completed handovers (released: ${s.released_count ?? 0})`}>
                        {s.assigned_total ?? 0} / {s.scanned_total ?? 0}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">{s.is_free ? s.suggested_qty : '—'}</td>
                    <td className="p-3 text-center">
                      {s.is_free ? (
                        <div className="flex gap-2 items-center justify-center">
                          <Input
                            type="number"
                            min={1}
                            max={free}
                            value={qtyByStaff[s.staff_id] ?? ''}
                            onChange={e => setQtyByStaff(prev => ({ ...prev, [s.staff_id]: e.target.value }))}
                            className="w-20 text-center"
                            data-testid={`qty-${s.staff_id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handover(s)}
                            disabled={submitting === s.staff_id || !free}
                            data-testid={`handover-${s.staff_id}`}
                          >
                            {submitting === s.staff_id ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-stone-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {showReleased && (
          <Card className="mb-4 border-amber-300" data-testid="released-handovers-card">
            <div className="p-4 border-b flex items-center justify-between bg-amber-50">
              <div>
                <div className="font-semibold text-amber-900">Released Handovers (revertible)</div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Use this when handovers were released by mistake. Revert returns status to ACTIVE so the PP staff can resume scanning.
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={loadReleased} disabled={releasedLoading} data-testid="reload-released-btn">
                  {releasedLoading ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Reload'}
                </Button>
                {releasedRows.length > 0 && (
                  <Button size="sm" onClick={bulkUnRelease} className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-un-release-btn">
                    Revert All Safe ({releasedRows.filter(r => r.un_release_safe).length})
                  </Button>
                )}
              </div>
            </div>
            {releasedLoading ? (
              <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline"/></div>
            ) : releasedRows.length === 0 ? (
              <div className="p-8 text-center text-stone-500 text-sm">No released handovers on this plan.</div>
            ) : (
              <table className="w-full text-sm" data-testid="released-handovers-table">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-3">Handover</th>
                    <th className="text-left p-3">Staff</th>
                    <th className="text-right p-3">Assigned</th>
                    <th className="text-right p-3">Scanned</th>
                    <th className="text-right p-3">Consumed USNs</th>
                    <th className="text-left p-3">Released At</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-center p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {releasedRows.map(h => (
                    <tr key={h.handover_id} className={`border-t ${!h.un_release_safe ? 'bg-rose-50' : ''}`}>
                      <td className="p-3 font-mono text-xs">{h.handover_id}</td>
                      <td className="p-3">{h.pp_staff_name || h.pp_staff_id}</td>
                      <td className="p-3 text-right">{h.qty_assigned}</td>
                      <td className="p-3 text-right">{h.qty_scanned}</td>
                      <td className="p-3 text-right">{h.consumed_usns_count}</td>
                      <td className="p-3 text-xs">{(h.released_at || '').replace('T', ' ').slice(0, 19)}</td>
                      <td className="p-3 text-center">
                        {h.un_release_safe ? (
                          <Badge variant="outline" className="text-xs">Safe to revert</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-rose-100 text-rose-800 border-rose-300">
                            Conflict ({h.peer_active_handovers_for_staff} active peer)
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Button size="sm" variant="outline" onClick={() => unRelease(h)} data-testid={`un-release-${h.handover_id}`}>
                          <Undo2 className="h-4 w-4 mr-1"/> Revert
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}

        {state.active_handovers?.length > 0 && (
          <Card className="mb-4">
            <div className="p-4 border-b font-semibold">Active Handovers</div>
            <table className="w-full text-sm" data-testid="active-handovers-table">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3">Handover</th>
                  <th className="text-left p-3">Staff</th>
                  <th className="text-right p-3">Assigned</th>
                  <th className="text-right p-3">Scanned</th>
                  <th className="text-right p-3">Remaining</th>
                  <th className="text-center p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {state.active_handovers.map(h => (
                  <tr key={h.handover_id} className="border-t">
                    <td className="p-3 font-mono text-xs">{h.handover_id}</td>
                    <td className="p-3">{h.pp_staff_name}</td>
                    <td className="p-3 text-right">{h.qty_assigned}</td>
                    <td className="p-3 text-right">{h.qty_scanned}</td>
                    <td className="p-3 text-right font-bold">{h.qty_assigned - h.qty_scanned}</td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="outline" onClick={() => release(h)} data-testid={`release-${h.handover_id}`}>
                        Release
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Reprint Combo Stickers — per session in this plan */}
        <Card data-testid="reprint-card">
          <div className="p-4 border-b font-semibold flex items-center justify-between">
            <span>Reprint Combo Stickers</span>
            <div className="flex items-center gap-2">
              <PrintMethodToggle screenKey="combo-distributor" />
              <Button
                size="sm"
                variant={usbDevice ? 'outline' : 'default'}
                onClick={connectUSBPrinter}
                disabled={!usbSupported}
                data-testid="connect-usb-btn"
              >
                <Usb className="h-4 w-4 mr-1" />
                {usbDevice ? `Connected: ${usbDevice.productName || 'USB Printer'}` : 'Connect USB printer'}
              </Button>
            </div>
          </div>
          {!sessions.length ? (
            <div className="p-8 text-center text-stone-500 text-sm">No sessions in this plan yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="reprint-sessions-table">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3">Session</th>
                  <th className="text-left p-3">Combo</th>
                  <th className="text-left p-3">Design / Shade / Size</th>
                  <th className="text-right p-3">Target</th>
                  <th className="text-right p-3">Assembled</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-center p-3">Reprint</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.session_id} className="border-t hover:bg-stone-50" data-testid={`reprint-row-${s.session_id}`}>
                    <td className="p-3 font-mono text-xs">{s.session_id}</td>
                    <td className="p-3 font-mono">{s.combo_barcode}</td>
                    <td className="p-3 text-xs">
                      <div>{s.design_name} • {s.shade} • {s.size}</div>
                      {s.description && <div className="text-stone-500">{s.description}</div>}
                    </td>
                    <td className="p-3 text-right">{s.target_quantity}</td>
                    <td className="p-3 text-right font-medium">{s.assembled_quantity}</td>
                    <td className="p-3 text-center"><Badge variant="outline" className="text-xs">{s.status}</Badge></td>
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={s.assembled_quantity === 0 || reprinting === s.session_id}
                        onClick={() => openReprintModal(s.session_id, s.combo_barcode)}
                        data-testid={`reprint-${s.session_id}`}
                      >
                        {reprinting === s.session_id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><Printer className="h-4 w-4 mr-1" /> Reprint…</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Box Sticker Alert Dialog (parity with V1 ComboAssemblyStation) */}
      <Dialog open={showBoxAlert} onOpenChange={setShowBoxAlert}>
        <DialogContent className="max-w-md" data-testid="box-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Product Labels Printed - Box Sticker Required
            </DialogTitle>
            <DialogDescription>
              Product labels have been printed. Now select the box label printer and print box labels.
            </DialogDescription>
          </DialogHeader>

          {pendingBoxData && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-800">Product Labels Printed</div>
                <div className="text-sm text-green-600">
                  {pendingBoxData.labelInfo.usn_count} combo USNs • {pendingBoxData.labelInfo.combo_name || pendingBoxData.labelInfo.combo_barcode}
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-medium text-blue-800 mb-2">Box Label Printer</div>
                {boxPrinterDevice ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Printer className="w-4 h-4" />
                      <span>{boxPrinterDevice.productName || 'USB Printer'}</span>
                      <span className="text-green-600 font-medium">Connected</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={connectBoxPrinter} className="text-blue-600 border-blue-300">
                      Change
                    </Button>
                  </div>
                ) : (
                  <Button onClick={connectBoxPrinter} variant="outline" className="w-full border-blue-300 text-blue-600 hover:bg-blue-100" data-testid="select-box-printer-btn">
                    <Printer className="w-4 h-4 mr-2" />
                    Select Box Label Printer
                  </Button>
                )}
              </div>
              <Alert className="bg-yellow-50 border-yellow-300">
                <Package className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">Ready to Print</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  {boxPrinterDevice
                    ? 'Box label printer is connected. Click "Print Box Labels" to proceed.'
                    : 'Please select the printer with box sticker roll loaded.'}
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleBoxPrintCancel} data-testid="skip-box-labels-btn">
              Skip Box Labels
            </Button>
            <Button
              onClick={handleBoxPrintConfirm}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!boxPrinterDevice}
              data-testid="print-box-labels-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Box Labels
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprint Modal — drill into per-handover items and pick the
          specific damaged stickers to reprint instead of reissuing the
          QR codes for every assembled USN in the session. */}
      <Dialog open={!!reprintModal} onOpenChange={(o) => { if (!o) { setReprintModal(null); setReprintSelected(new Set()); } }}>
        <DialogContent
          className="max-w-3xl"
          data-testid={reprintModal ? `reprint-modal-${reprintModal.sessionId}` : 'reprint-modal'}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-emerald-700" />
              Reprint Combo Stickers
              {reprintModal?.sessionInfo?.combo_barcode && (
                <span className="font-mono text-sm text-stone-500">· {reprintModal.sessionInfo.combo_barcode}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              Pick the staff handover and tick only the damaged stickers.
              Reprinting issues a fresh QR code per ticked USN — every other
              sticker in the session is left untouched.
            </DialogDescription>
          </DialogHeader>

          {reprintModal?.loading ? (
            <div className="p-12 text-center text-stone-500">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading handovers…
            </div>
          ) : !reprintModal?.handovers?.length ? (
            <div className="p-8 text-center text-stone-500">
              No handovers found for this session.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-4">
              {reprintModal.handovers.map((h, hIdx) => {
                const items = h.items || [];
                const codes = items.map((it) => it.real_usn_code || it.combo_usn).filter(Boolean);
                const allSelected = codes.length > 0 && codes.every((c) => reprintSelected.has(c));
                const someSelected = codes.some((c) => reprintSelected.has(c));
                const handoverKey = h.handover_id || `legacy-${hIdx}`;
                return (
                  <div
                    key={handoverKey}
                    className="border border-stone-200 rounded-lg overflow-hidden"
                    data-testid={`reprint-handover-${handoverKey}`}
                  >
                    <div className="px-3 py-2 bg-stone-100 flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-emerald-600"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleReprintHandover(h)}
                          data-testid={`reprint-handover-toggle-${handoverKey}`}
                        />
                        <span className="font-semibold text-stone-800">
                          {h.pp_staff_name || 'Unknown staff'}
                          {h.pp_staff_id && (
                            <span className="ml-1 font-mono text-xs text-stone-500">({h.pp_staff_id})</span>
                          )}
                        </span>
                        {h.handover_id && (
                          <span className="font-mono text-xs text-stone-500 ml-1">{h.handover_id}</span>
                        )}
                      </label>
                      <div className="text-xs text-stone-600 flex items-center gap-3">
                        <span>Assembled: <strong>{h.qty_assembled}</strong></span>
                        {typeof h.qty_assigned === 'number' && (
                          <span>Assigned: {h.qty_assigned}</span>
                        )}
                        <span>Printed: {h.label_printed_count}</span>
                      </div>
                    </div>
                    {items.length === 0 ? (
                      <div className="p-3 text-sm text-stone-500">No assembled items.</div>
                    ) : (
                      <>
                        {items.some((it) => !it.real_usn_code) && (
                          <div
                            className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800"
                            data-testid={`reprint-handover-needs-allocation-${handoverKey}`}
                          >
                            <strong>⚠ {items.filter((it) => !it.real_usn_code).length} sticker(s) need allocation.</strong>{' '}
                            Their <span className="font-mono">Real USN</span> shows <span className="font-mono">—</span> because
                            allocation didn't complete (usually a printer-disconnect during the original print).
                            Tick them and click <strong>Reprint Selected</strong> — the system will allocate fresh USNs and print.
                          </div>
                        )}
                        <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-500">
                          <tr>
                            <th className="w-8 p-2"></th>
                            <th className="text-left p-2">#</th>
                            <th className="text-left p-2">Real USN</th>
                            <th className="text-left p-2">Combo USN</th>
                            <th className="text-center p-2">Pair</th>
                            <th className="text-center p-2">Printed</th>
                            <th className="text-center p-2">Bin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, iIdx) => {
                            const code = it.real_usn_code || it.combo_usn;
                            const isPutAway = !!it.put_away_bin;
                            return (
                              <tr
                                key={code || iIdx}
                                className="border-t border-stone-100"
                                data-testid={`reprint-item-${code}`}
                              >
                                <td className="p-2 text-center">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-emerald-600"
                                    checked={reprintSelected.has(code)}
                                    onChange={() => toggleReprintUsn(code)}
                                    disabled={!code}
                                    data-testid={`reprint-item-cb-${code}`}
                                  />
                                </td>
                                <td className="p-2 text-stone-500">{it.pair_number || iIdx + 1}</td>
                                <td className="p-2 font-mono text-emerald-700">{it.real_usn_code || '—'}</td>
                                <td className="p-2 font-mono text-stone-500">{it.combo_usn}</td>
                                <td className="p-2 text-center">{it.pair_number || '—'}</td>
                                <td className="p-2 text-center">
                                  {it.label_printed
                                    ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Yes</Badge>
                                    : <Badge variant="outline" className="text-stone-500">No</Badge>}
                                </td>
                                <td className={`p-2 text-center font-mono text-xs ${isPutAway ? 'text-emerald-700' : 'text-stone-400'}`}>
                                  {it.put_away_bin || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
            <div className="text-sm text-stone-600">
              <strong>{reprintSelected.size}</strong> sticker(s) selected to reprint
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setReprintModal(null); setReprintSelected(new Set()); }} data-testid="reprint-modal-cancel">
                Cancel
              </Button>
              <Button
                onClick={reprintSelectedUsns}
                disabled={reprintSelected.size === 0 || (reprintModal && reprinting === reprintModal.sessionId)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="reprint-modal-confirm"
              >
                {reprintModal && reprinting === reprintModal.sessionId
                  ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Reprinting…</>)
                  : (<><Printer className="w-4 h-4 mr-1" /> Reprint Selected ({reprintSelected.size})</>)}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
